import React from 'react';
import PropTypes from 'prop-types';
import Image from 'components/image';
import Link from 'components/link';
import LinkButton, {
  sizes as linkButtonSizes,
  arrowDirections as linkButtonArrowDirections,
} from 'components/link-button';
import PageHeading from 'components/page-heading';
import PoorText from 'components/poor-text';
import ParallaxHeroBackground from 'components/parallax-hero-background';
import SpecimenPageHeroFact from './specimen-page-hero-fact';
import { calculateInterpolatedValue } from 'js/calc-utils';
import { Parallax } from 'react-parallax';
import { imageSourceBreakpoints } from 'js/breakpoints';

const SpecimenPageHero = ({
  heading,
  introHtml,
  image,
  allSpeciesLink,
  backgroundColor,
  nutritionalInformationHtml,
  catchRangeHtml,
  seasonHtml,
  lang,
}) => {
  return (
    <div className="specimen-page-hero">
      <ParallaxHeroBackground
        backgroundColor={backgroundColor}
        useParallaxOnMobile={false}
        className="specimen-page-hero__background"
      />
      <div className="specimen-page-hero__container">
        <div className="specimen-page-hero__content">
          {allSpeciesLink && (
            <LinkButton
              className="specimen-page-hero__all-species-link"
              size={linkButtonSizes.small}
              arrowDirection={linkButtonArrowDirections.left}
              {...allSpeciesLink}
            />
          )}
          {heading && (
            <PageHeading
              text={heading}
              className="specimen-page-hero__heading"
            />
          )}
          <div className="specimen-page-hero__text-holder">
            {introHtml && (
              <PoorText
                text={introHtml}
                className="specimen-page-hero__intro poor-text_in-specimen-page-hero"
              />
            )}
          </div>
        </div>
        <div className="specimen-page-hero__image-holder">
          <Parallax
            style={{
              overflow: 'visible',
            }}
            renderLayer={percentage => {
              const maxPercentage = 1.3;
              const maxImageColPadding = 60;
              let imageColPadding = 0;

              if (percentage > maxPercentage) {
                imageColPadding = maxImageColPadding;
              } else {
                imageColPadding = calculateInterpolatedValue(
                  percentage,
                  maxImageColPadding,
                  maxPercentage
                );
              }

              return (
                <div
                  style={{
                    paddingLeft: maxImageColPadding - imageColPadding,
                  }}
                  className="specimen-page-hero__image-holder-parallax-indent-element"
                >
                  <div className="specimen-page-hero__image-holder-overlay">
                    <Image
                      {...image}
                      sources={[
                        {
                          width: 406,
                          height: 364,
                          media: imageSourceBreakpoints.xs,
                        },
                        {
                          width: 281,
                          height: 251,
                          media: imageSourceBreakpoints.s,
                        },
                        {
                          width: 424,
                          height: 380,
                          media: imageSourceBreakpoints.m,
                        },
                        {
                          width: 620,
                          height: 555,
                          media: imageSourceBreakpoints.l,
                        },
                      ]}
                      fetchPriority="high"
                      className="specimen-page-hero__image image_size_specimen-page-hero"
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>
        <div className="specimen-page-hero__facts">
          {nutritionalInformationHtml && (
            <SpecimenPageHeroFact
              heading={lang.nutritionalInformationLabel}
              textHtml={nutritionalInformationHtml}
            />
          )}
          {catchRangeHtml && (
            <SpecimenPageHeroFact
              heading={lang.catchRangeLabel}
              textHtml={catchRangeHtml}
            />
          )}
          {seasonHtml && (
            <SpecimenPageHeroFact
              heading={lang.seasonLabel}
              textHtml={seasonHtml}
            />
          )}
        </div>
      </div>
    </div>
  );
};

SpecimenPageHero.propTypes = {
  heading: PropTypes.string,
  introHtml: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  allSpeciesLink: PropTypes.exact(Link.propTypes),
  backgroundColor: PropTypes.string,
  nutritionalInformationHtml: PropTypes.string,
  catchRangeHtml: PropTypes.string,
  seasonHtml: PropTypes.string,
  lang: PropTypes.shape({
    nutritionalInformationLabel: PropTypes.string.isRequired,
    catchRangeLabel: PropTypes.string.isRequired,
    seasonLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export default SpecimenPageHero;
